import Project from '@theme/global-functions';

class RunArchives {
  constructor(element) {
    this.element = element;
    this.contentWrapper = this.element.querySelector(
      '.run-archives-block__content-wrapper'
    );
    this.postsPerPage = parseInt(this.element.dataset.postsPerPage, 10) || 10;
    this.selectedCategory = this.element.dataset.runCategory || '';
    this.isLoading = false;

    const sortButton = this.element.querySelector('#sort-order-toggle');
    this.sortOrder = sortButton ? sortButton.dataset.order : 'DESC';
  }

  init() {
    this.initDropdowns();
    this.initPagination();
    this.initCombobox();
    this.initSortButton();
  }

  initDropdowns() {
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.run-archives-block__dropdown')) {
        this.closeAllDropdowns();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAllDropdowns();
      }
    });
    this.setupDropdownEventListeners();
  }

  setupDropdownEventListeners() {
    const dropdowns = this.element.querySelectorAll(
      '.run-archives-block__dropdown'
    );
    dropdowns.forEach((dropdown) => {
      const button = dropdown.querySelector(
        '.run-archives-block__dropdown-button'
      );
      const close = dropdown.querySelector(
          '.run-archives-block__close'
      );
      const menu = dropdown.querySelector(
        '.run-archives-block__dropdown-content'
      );
      if (!button || !menu) return;
      button.addEventListener('click', (e) => {
        const isExpanded =
          e.currentTarget.getAttribute('aria-expanded') === 'true';
        this.closeAllDropdowns();
        if (!isExpanded) {
          this.toggleDropdown(button, menu, true);
        }
      });
        close.addEventListener('click', (e) => {
            console.log('clicked')
            this.closeAllDropdowns();
        });
        if (window.innerWidth >= 769) {
            dropdown.addEventListener('focusout', (e) => {
                if (!dropdown.contains(e.relatedTarget)) {
                    this.toggleDropdown(button, menu, false);
                }
            });
        }

    });
  }

  initPagination() {
    this.element.addEventListener('click', (e) => {
      const paginationLink = e.target.closest(
        '.run-archives-block__pagination a'
      );
      if (paginationLink) {
        e.preventDefault();
        if (this.isLoading) return;

        const url = new URL(paginationLink.href);
        const page = url.searchParams.get('paged') || 1;
        this.loadPage(page);
      }
    });
  }

  initCombobox() {
    this.element.addEventListener('click', (e) => {
      const button = e.target.closest('#combobox-button');
      const option = e.target.closest('.combobox-option');

      if (button) {
        this.toggleCombobox(
          button,
          button.getAttribute('aria-expanded') !== 'true'
        );
      } else if (option) {
        this.selectComboboxOption(option);
      } else {
        const openButton = this.element.querySelector(
          '#combobox-button[aria-expanded="true"]'
        );
        if (openButton) {
          this.toggleCombobox(openButton, false);
        }
      }
    });

    this.element.addEventListener('keydown', (e) => {
      const combobox = e.target.closest('.combobox');
      if (!combobox) return;

      const button = combobox.querySelector('#combobox-button');
      const listbox = combobox.querySelector('#combobox-listbox');
      const isExpanded = button.getAttribute('aria-expanded') === 'true';

      if (e.key === 'Escape') {
        if (isExpanded) {
          this.toggleCombobox(button, false);
          button.focus();
        }
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (e.target === button) {
          this.toggleCombobox(button, !isExpanded);
        } else if (
          isExpanded &&
          e.target.classList.contains('combobox-option')
        ) {
          this.selectComboboxOption(e.target);
        }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!isExpanded) {
          this.toggleCombobox(button, true);
        }
        const options = Array.from(
          listbox.querySelectorAll('.combobox-option')
        );
        const currentIndex = options.findIndex(
          (opt) => opt === document.activeElement
        );
        let nextIndex = currentIndex + (e.key === 'ArrowDown' ? 1 : -1);

        if (nextIndex >= options.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = options.length - 1;

        options[nextIndex]?.focus();
      }
    });

    this.element.addEventListener('focusout', (e) => {
      const wrapper = e.target.closest('.combobox-wrapper');

      if (wrapper && !wrapper.contains(e.relatedTarget)) {
        const button = wrapper.querySelector('#combobox-button');
        if (button && button.getAttribute('aria-expanded') === 'true') {
          this.toggleCombobox(button, false);
        }
      }
    });
  }

  toggleCombobox(button, forceOpen) {
    const listbox = button.nextElementSibling;
    button.setAttribute('aria-expanded', String(forceOpen));
    listbox.hidden = !forceOpen;
  }

  selectComboboxOption(option) {
    if (this.isLoading) return;

    const button = option
      .closest('.combobox')
      .querySelector('#combobox-button');
    const listbox = option.parentElement;
    const allOptions = listbox.querySelectorAll('.combobox-option');
    const newValue = parseInt(option.dataset.value, 10);

    allOptions.forEach((opt) => opt.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');

    this.toggleCombobox(button, false);
    button.focus();

    if (this.postsPerPage !== newValue) {
      this.postsPerPage = newValue;
      this.loadPage(1);
    }
  }

  initSortButton() {
    this.element.addEventListener('click', (e) => {
      if (e.target.id === 'sort-order-toggle') {
        if (this.isLoading) return;

        // Zmień kierunek sortowania
        this.sortOrder = this.sortOrder === 'DESC' ? 'ASC' : 'DESC';

        // Przeładuj widok, wracając do pierwszej strony
        this.loadPage(1);
      }
    });
  }

  async loadPage(page) {
    this.isLoading = true;
    this.element.classList.add('is-loading');

    const formData = new FormData();
    formData.append('action', 'load_archives_page');
    formData.append('nonce', window.wp_app_vars.nonce);
    formData.append('page', page);
    formData.append('posts_per_page', this.postsPerPage);
    formData.append('run_category', this.selectedCategory);
    formData.append('order', this.sortOrder);

    try {
      const response = await fetch(window.wp_app_vars.ajax_url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok.');
      }

      const data = await response.json();

      if (data.success) {
        this.contentWrapper.innerHTML = data.data.posts;
        this.setupDropdownEventListeners();
        this.initPagination();
        this.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        throw new Error('AJAX request failed.');
      }
    } catch (error) {
      console.error('Error fetching new page:', error);
      this.contentWrapper.innerHTML = `<p>Wystąpił błąd podczas ładowania danych.</p>`;
    } finally {
      this.isLoading = false;
      this.element.classList.remove('is-loading');
    }
  }

  toggleDropdown(button, menu, forceOpen) {
    button.setAttribute('aria-expanded', String(forceOpen));
    menu.hidden = !forceOpen;
  }

  closeAllDropdowns() {
    const activeDropdowns = this.element.querySelectorAll(
      '.run-archives-block__dropdown-button[aria-expanded="true"]'
    );
    activeDropdowns.forEach((button) => {
      const menu = button.nextElementSibling;
      if (menu) {
        this.toggleDropdown(button, menu, false);
      }
    });
  }
}

Project.ready(() => {
  document.querySelectorAll('.run-archives-block').forEach((module) => {
    const instance = new RunArchives(module);
    instance.init();
  });
});

if (typeof acf !== 'undefined') {
  acf.addAction('ready', (el) => {
    document.querySelectorAll('.run-archives-block').forEach((blockElement) => {
      const instance = new RunArchives(blockElement);
      instance.init();
    });
  });

  acf.addAction('append', (el) => {
    document.querySelectorAll('.run-archives-block').forEach((blockElement) => {
      const instance = new RunArchives(blockElement);
      instance.init();
    });
  });
}

export default RunArchives;
